import React from 'react';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';

import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';

import helper from '@/helper';
import config from '@/configs/config';
import useCountDown from '@/hooks/useCountDown';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: 10,
    borderBottom: '1px solid rgb(217, 217, 217)',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: 'rgb(217, 217, 217)',
    },
  },
  cardMedia: {
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
  },
  label: {
    width: 'auto',
    paddingTop: theme.spacing(0.2),
    paddingBottom: theme.spacing(0.2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    textAlign: 'center',
    borderRadius: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
  },
  successLabel: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },
  warningLabel: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
  },
  btn: {
    width: 'auto',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    textAlign: 'center',
    borderRadius: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
    cursor: 'point',
  },
  checkoutBtn: {
    backgroundColor: theme.palette.error.dark,
    color: theme.palette.common.white,
  },
  shareBtn: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.primary.main,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const WatchedGroupPreOrderCard = ({
  data,
  hanldeShare: triggerShare,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [countDown, isStartCountDown, setCountDownEndTime] = useCountDown();
  const countDownLabelColor = Number(countDown.days) >= 1 ? classes.successLabel : classes.warningLabel; // eslint-disable-line max-len
  const now = new Date().getTime();

  const {
    preOrder,
    ...group
  } = data;

  const basicPrice = helper.getAppropriatePriceByRules(preOrder.hierarchyPriceRules, 0);

  const goToGroupPreOrderDetail = React.useCallback((event) => {
    event.stopPropagation();
    history.push({
      pathname: `/pre-orders/${group.preOrderId}`,
      search: `?${queryString.stringify({
        promotionCode: group.promotionCode,
      })}`,
    });
  }, [history, group]);

  React.useEffect(() => {
    if (!preOrder && preOrder.registerEndTime > now) return;
    setCountDownEndTime(preOrder.registerEndTime);
  }, [preOrder, now, setCountDownEndTime]);

  return (
    <Box className={classes.card} width={1} onClick={goToGroupPreOrderDetail}>
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <Box position="relative" width={100} height={100}>
            <Box
              width={1}
              pb="100%"
              bgcolor="grey.300"
            />
            <Box
              position="absolute"
              width={1}
              height={1}
              top={0}
              left={0}
            >
              {
                preOrder.coverImage && (
                  <img
                    style={{ objectFit: 'cover' }}
                    width="100%"
                    height="100%"
                    src={`${config.RESOURCE_URI}${preOrder.coverImage}`}
                    alt=""
                  />
                )
              }
            </Box>
          </Box>
        </Grid>
        <Grid item xs>
          <Box>
            <Grid container justify="space-between">
              <Grid item>
                {
                  isStartCountDown && (
                    <Typography
                      className={
                        clsx(
                          classes.label,
                          countDownLabelColor
                        )
                      }
                      noWrap
                    >
                      {`倒數: ${countDown.days}天${countDown.hours}時${countDown.minutes}分${countDown.seconds}秒`}
                    </Typography>
                  )
                }
              </Grid>

            </Grid>
            <Box my={1}>
              <Grid container alignItems="flex-end">
                <Grid item xs={12}>
                  <Box
                    fontSize="h6.fontSize"
                    fontWeight="fontWeightBold"
                  >
                    {preOrder.title}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    fontSize="subtitle1.fontSize"
                    fontWeight="fontWeightMedium"
                  >
                    {`${preOrder.slogan}(${preOrder.spec})`}
                  </Box>
                </Grid>
              </Grid>
              <Grid container justify="space-between">
                <Grid item>
                  <Box
                    fontSize="body2.fontSize"
                    fontWeight="fontWeightMedium"
                  >
                    {`原價: $${preOrder.priceOriginal}`}
                  </Box>
                </Grid>
                <Grid item>
                  <Box
                    fontSize="body2.fontSize"
                    fontWeight="fontWeightMedium"
                  >
                    <Grid container>
                      <Grid item>
                        <Box
                          fontSize="body2.fontSize"
                          fontWeight="fontWeightMedium"
                        >
                          預購優惠價:
                        </Box>
                      </Grid>
                      <Grid item>
                        <Box
                          fontSize="body2.fontSize"
                          fontWeight="fontWeightMedium"
                          color="error.dark"
                        >
                          {`$${basicPrice}`}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
              <Grid container justify="space-between">
                <Grid item>
                  <Box
                    fontSize="body2.fontSize"
                    fontWeight="fontWeightMedium"
                  >
                    {`參與人數: ${group.joinedCustomerCount || 0}`}
                  </Box>
                </Grid>
                <Grid item>
                  <Box
                    fontSize="body2.fontSize"
                    fontWeight="fontWeightMedium"
                  >
                    {`團購組數: ${group.joinedTotalCount || 0}`}
                  </Box>
                </Grid>
              </Grid>
              <Grid container justify="space-between">
                <Grid item>
                  <Box
                    fontSize="body2.fontSize"
                    fontWeight="fontWeightMedium"
                  >
                    {`回饋金: ${group.rebatePercentage || 0}%(持續累計中)`}
                  </Box>
                </Grid>
                <Grid item>
                  <Box
                    fontSize="body2.fontSize"
                    fontWeight="fontWeightMedium"
                  >
                    {`取貨日: ${(new Date(preOrder.preOrderArrivedNotificationTime)).toLocaleDateString()}`}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>

      </Grid>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={6}>
          <Grid container justify="space-between" alignItems="center">
            <Grid item>
              <Box
                fontSize="subtitle1.fontSize"
                fontWeight="fontWeightMedium"
              >
                {`團碼：${group.promotionCode}`}
              </Box>
            </Grid>
            <Grid item>
              <Box
                height={1}
                fontSize="subtitle1.fontSize"
                fontWeight="fontWeightMedium"
                justifyContent="center"
              >
                {`團名：${group.name}`}
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container justify="flex-end" alignItems="center">
            <Grid item>
              <Box
                mx={0.5}
                className={
                clsx(
                  classes.btn,
                  classes.shareBtn
                )
              }
                onClick={(event) => {
                  event.stopPropagation();
                  triggerShare({
                    preOrderId: group.preOrderId,
                    promotionCode: group.promotionCode,
                  });
                }}
              >
                勸敗入團
              </Box>
            </Grid>
            <Grid item>
              <Box
                mx={0.5}
                className={
                clsx(
                  classes.btn,
                  classes.checkoutBtn
                )
              }
                onClick={goToGroupPreOrderDetail}
              >
                手刀下單
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WatchedGroupPreOrderCard;
